import React, { useState, useEffect } from 'react';
import { Clock, Calendar, Trophy, Users, HeartHandshake, Star, Award, TrendingUp } from 'lucide-react';
import { t, getCurrentLanguage } from '../utils/translations';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import api from '../components/api';

const HistoriquePage = () => {
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const [historiqueData, setHistoriqueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Écouter les changements de langue
  useEffect(() => {
    // Détecter la langue depuis l'URL au chargement initial
    const detectLanguageFromURL = () => {
      const path = window.location.pathname;
      if (path.startsWith('/en/')) {
        return 'en';
      } else if (path.startsWith('/fr/')) {
        return 'fr';
      }
      return 'fr'; // Par défaut
    };

    const initialLang = detectLanguageFromURL();
    setCurrentLanguage(initialLang);
    
    // Forcer la mise à jour de i18n si nécessaire
    if (window.i18n && window.i18n.language !== initialLang) {
      window.i18n.changeLanguage(initialLang);
      localStorage.setItem('i18nextLng', initialLang);
    }

    const handleLanguageChange = () => {
      const newLang = getCurrentLanguage();
      setCurrentLanguage(newLang);
    };

    // Écouter l'événement de changement de langue
    window.addEventListener('languageChanged', handleLanguageChange);

    // Écouter aussi les changements i18n
    const i18nListener = () => {
      const newLang = getCurrentLanguage();
      setCurrentLanguage(newLang);
    };

    if (window.i18n) {
      window.i18n.on('languageChanged', i18nListener);
    }

    // Écouter les changements d'URL
    const handleURLChange = () => {
      const urlLang = detectLanguageFromURL();
      if (urlLang !== currentLanguage) {
        setCurrentLanguage(urlLang);
        if (window.i18n && window.i18n.language !== urlLang) {
          window.i18n.changeLanguage(urlLang);
        }
      }
    };

    window.addEventListener('popstate', handleURLChange);

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('popstate', handleURLChange);
      if (window.i18n) {
        window.i18n.off('languageChanged', i18nListener);
      }
    };
  }, [currentLanguage]);

  // Récupérer les données de la page Histoire
  useEffect(() => {
    const fetchHistoriqueData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/historique-page/');
        setHistoriqueData(response.data);
        setError(null);
      } catch (err) {
        setError('Erreur lors du chargement de la page');
        console.error('Erreur API Histoire:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistoriqueData();
  }, []);

  // Fonction pour obtenir le contenu traduit
  const getTranslatedContent = (frContent, enContent) => {
    if (currentLanguage === 'en' && enContent) {
      return enContent;
    }
    return frContent || enContent || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#089556] mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !historiqueData) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex-grow flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Erreur de chargement</h2>
            <p className="text-gray-600 mb-6">{error || 'Aucune donnée disponible'}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-[#089556] text-white px-6 py-3 rounded-lg hover:bg-[#078a4a] transition-colors duration-200"
            >
              Réessayer
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Enhanced Hero Section */}
      <section className="relative h-[400px] mb-16 overflow-hidden">
        {/* Enhanced Molecular Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#089556] via-[#0b6f47] to-[#07D1EB]">
          <div className="absolute inset-0 opacity-[0.3]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 20% 30%, rgba(255,255,255,0.8) 3px, transparent 3px),
                radial-gradient(circle at 80% 70%, rgba(255,255,255,0.6) 2.5px, transparent 2.5px),
                radial-gradient(circle at 40% 80%, rgba(255,255,255,0.7) 2px, transparent 2px),
                radial-gradient(circle at 70% 20%, rgba(255,255,255,0.5) 3.5px, transparent 3.5px),
                radial-gradient(circle at 90% 50%, rgba(255,255,255,0.8) 2.8px, transparent 2.8px),
                radial-gradient(circle at 30% 60%, rgba(255,255,255,0.6) 2.2px, transparent 2.2px)
              `,
              backgroundSize: '200px 200px, 160px 160px, 220px 220px, 180px 180px, 190px 190px, 150px 150px'
            }}></div>
            
            {/* Enhanced Connection Lines */}
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(35deg, transparent 48%, rgba(255,255,255,0.4) 49%, rgba(255,255,255,0.4) 51%, transparent 52%),
                linear-gradient(-35deg, transparent 48%, rgba(255,255,255,0.3) 49%, rgba(255,255,255,0.3) 51%, transparent 52%),
                linear-gradient(70deg, transparent 48%, rgba(255,255,255,0.35) 49%, rgba(255,255,255,0.35) 51%, transparent 52%)
              `,
              backgroundSize: '350px 350px, 320px 320px, 380px 380px',
              opacity: '0.5'
            }}></div>
          </div>

          {/* Enhanced Floating Molecules */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/6 left-1/4 w-5 h-5 bg-white rounded-full opacity-20 animate-pulse shadow-lg shadow-white/40"></div>
            <div className="absolute top-2/3 right-1/4 w-4 h-4 bg-white rounded-full opacity-30 animate-bounce shadow-lg shadow-white/50"></div>
            <div className="absolute top-1/2 left-3/4 w-4.5 h-4.5 bg-white rounded-full opacity-25 animate-pulse shadow-lg shadow-white/45"></div>
            <div className="absolute top-1/4 right-1/3 w-3 h-3 bg-white rounded-full opacity-35 animate-bounce shadow-lg shadow-white/40"></div>
            <div className="absolute bottom-1/6 left-1/6 w-3.5 h-3.5 bg-white rounded-full opacity-25 animate-pulse shadow-lg shadow-white/50"></div>
          </div>
        </div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center relative z-10">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-8 shadow-xl border border-white/30">
              <Clock className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight drop-shadow-lg mb-4">
              {getTranslatedContent(historiqueData.hero_title_fr, historiqueData.hero_title_en)}
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed drop-shadow-sm">
              {getTranslatedContent(historiqueData.hero_subtitle_fr, historiqueData.hero_subtitle_en)}
            </p>
            <div className="w-32 h-1 bg-white/60 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {/* Enhanced Notre Parcours Section */}
        <section className="relative mb-20 overflow-hidden">
          {/* Molecular Background */}
          <div className="absolute inset-0 opacity-[0.08]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 25% 25%, #089556 2px, transparent 2px),
                radial-gradient(circle at 75% 75%, #07D1EB 1.5px, transparent 1.5px),
                radial-gradient(circle at 50% 10%, #089556 1px, transparent 1px)
              `,
              backgroundSize: '150px 150px, 120px 120px, 180px 180px'
            }}></div>
          </div>

          <div className="relative z-10 bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
            <div className="flex items-center gap-6 mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg">
                <HeartHandshake className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-[#089556]">
                  {getTranslatedContent(historiqueData.parcours_title_fr, historiqueData.parcours_title_en)}
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-full mt-2"></div>
              </div>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 text-justify">
              <div 
                dangerouslySetInnerHTML={{
                  __html: getTranslatedContent(historiqueData.parcours_content_fr, historiqueData.parcours_content_en)
                }}
              />
            </div>
          </div>
        </section>

        {/* Enhanced Statistiques et Impact Section */}
        <section className="relative mb-20 overflow-hidden">
          {/* Molecular Background */}
          <div className="absolute inset-0 opacity-[0.06]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 30% 70%, #089556 2px, transparent 2px),
                radial-gradient(circle at 70% 30%, #07D1EB 1.5px, transparent 1.5px)
              `,
              backgroundSize: '170px 170px, 140px 140px'
            }}></div>
          </div>

          <div className="relative z-10">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full mb-6 shadow-lg">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-4xl font-bold text-[#089556] mb-4">
                {getTranslatedContent(historiqueData.stats_title_fr, historiqueData.stats_title_en)}
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed text-justify">
                {getTranslatedContent(historiqueData.stats_subtitle_fr, historiqueData.stats_subtitle_en)}
              </p>
              <div className="w-24 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] mx-auto mt-6 rounded-full"></div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {getTranslatedContent(historiqueData.stats_items_fr, historiqueData.stats_items_en)?.map((stat, index) => (
                <div key={index} className="group relative bg-white/90 backdrop-blur-sm rounded-2xl p-8 text-center shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${stat.gradient} rounded-t-2xl`}></div>
                  
                  <div className="text-[#089556] mx-auto w-fit mb-6 mt-2">
                    {stat.icon === 'Users' && <Users className="h-8 w-8" />}
                    {stat.icon === 'Trophy' && <Trophy className="h-8 w-8" />}
                    {stat.icon === 'Calendar' && <Calendar className="h-8 w-8" />}
                  </div>
                  <div className="text-5xl font-bold text-[#089556] mb-3">{stat.value}</div>
                  <div className="text-xl font-semibold text-gray-800 mb-2 text-justify">{stat.label}</div>
                  <div className="text-gray-600 text-sm text-justify">{stat.description}</div>

                  {/* Decorative Elements */}
                  <div className="absolute top-4 right-4 w-8 h-8 opacity-10">
                    <div className="w-2 h-2 bg-[#07D1EB] rounded-full absolute top-0 left-0 animate-pulse"></div>
                    <div className="w-1.5 h-1.5 bg-[#089556] rounded-full absolute top-2 right-0 animate-pulse" style={{animationDelay: '0.5s'}}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Enhanced Vision d'Avenir Section */}
        <section className="relative mb-20 overflow-hidden">
          {/* Molecular Background */}
          <div className="absolute inset-0 opacity-[0.05]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 40% 60%, #089556 2px, transparent 2px),
                radial-gradient(circle at 60% 40%, #07D1EB 1.5px, transparent 1.5px)
              `,
              backgroundSize: '180px 180px, 150px 150px'
            }}></div>
          </div>

          <div className="relative z-10 bg-gradient-to-br from-[#089556]/10 via-[#089556]/5 to-[#07D1EB]/10 p-8 rounded-2xl shadow-lg border border-white/20">
            <div className="flex items-center gap-6 mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg">
                <Star className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-[#089556]">
                  {getTranslatedContent(historiqueData.vision_title_fr, historiqueData.vision_title_en)}
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-full mt-2"></div>
              </div>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 text-justify">
              <div 
                className="text-lg leading-relaxed text-justify mb-6"
                dangerouslySetInnerHTML={{
                  __html: getTranslatedContent(historiqueData.vision_content_fr, historiqueData.vision_content_en)
                }}
              />
              
              {/* Enhanced Goals Grid */}
              <div className="grid md:grid-cols-2 gap-6 mt-8">
                {getTranslatedContent(historiqueData.vision_goals_fr, historiqueData.vision_goals_en)?.map((goal, index) => (
                  <div key={index} className="bg-white/60 backdrop-blur-sm p-6 rounded-xl shadow-sm border border-white/20">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="text-[#089556]">
                        {goal.icon === 'Award' && <Award className="h-5 w-5" />}
                        {goal.icon === 'TrendingUp' && <TrendingUp className="h-5 w-5" />}
                        {goal.icon === 'HeartHandshake' && <HeartHandshake className="h-5 w-5" />}
                        {goal.icon === 'Users' && <Users className="h-5 w-5" />}
                      </div>
                      <h4 className="text-lg font-semibold text-[#089556] text-justify">{goal.title}</h4>
                    </div>
                    <p className="text-gray-700 text-justify">{goal.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Call to Action Section */}
        <section className="relative mb-20 overflow-hidden">
          {/* Molecular Background */}
          <div className="absolute inset-0 opacity-[0.08]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 20% 80%, #089556 2px, transparent 2px),
                radial-gradient(circle at 80% 20%, #07D1EB 1.5px, transparent 1.5px)
              `,
              backgroundSize: '160px 160px, 130px 130px'
            }}></div>
          </div>

          <div className="relative z-10 text-center py-12 bg-gradient-to-br from-[#089556]/15 via-[#089556]/10 to-[#07D1EB]/15 rounded-2xl shadow-lg border border-white/20">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full mb-8 shadow-xl">
              <HeartHandshake className="h-10 w-10 text-white" />
            </div>
            <h3 className="text-3xl font-bold text-[#089556] mb-6">
              {getTranslatedContent(historiqueData.cta_title_fr, historiqueData.cta_title_en)}
            </h3>
            <div className="w-24 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] mx-auto mb-6 rounded-full"></div>
            <div 
              className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed text-justify mb-8"
              dangerouslySetInnerHTML={{
                __html: getTranslatedContent(historiqueData.cta_content_fr, historiqueData.cta_content_en)
              }}
            />
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white font-semibold rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300">
                <HeartHandshake className="w-5 h-5 mr-3" />
                {getTranslatedContent(historiqueData.cta_donate_button_fr, historiqueData.cta_donate_button_en)}
              </button>
              <button className="inline-flex items-center px-8 py-4 bg-white/80 backdrop-blur-sm text-[#089556] font-semibold rounded-xl border-2 border-[#089556]/20 hover:bg-[#089556]/10 hover:scale-105 transition-all duration-300">
                <Users className="w-5 h-5 mr-3" />
                {getTranslatedContent(historiqueData.cta_volunteer_button_fr, historiqueData.cta_volunteer_button_en)}
              </button>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-6 right-6 w-12 h-12 opacity-10">
              <div className="w-3 h-3 bg-[#07D1EB] rounded-full absolute top-0 left-0 animate-pulse"></div>
              <div className="w-2 h-2 bg-[#089556] rounded-full absolute top-3 right-0 animate-pulse" style={{animationDelay: '0.5s'}}></div>
              <div className="w-2.5 h-2.5 bg-[#07D1EB] rounded-full absolute bottom-0 left-3 animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HistoriquePage;